import type { MutableRefObject } from 'react'
import type VectorSource from 'ol/source/Vector'
import type { Geometry, Point } from 'ol/geom'
import type { Layer } from 'ol/layer'
import type OlMap from 'ol/Map'
import { FormActionButton, FormFooter } from '@workwave-tidal/core/form'
import { useCallback } from 'react'
import { gis } from '@/server-data'
import { useControllerById } from '../../../hooks/useController'
import { useTexts } from '../../../useTexts'
import { form } from '../formState'

type Props = {
  map: MutableRefObject<OlMap | undefined>
  pinId:
    | 'depotFormPin'
    | 'placeFormPin'
    | 'geofenceFormPin'
    | 'orderForm_pickup'
    | 'orderForm_service'
    | 'orderForm_delivery'
  onSubmit: () => Promise<void>
}

export function Footer(props: Props) {
  const { map, pinId, onSubmit } = props

  const { close, updateData } = useControllerById(pinId)
  const {
    api,
    meta: { status },
  } = form.useForm()
  const { field: newLocationField } = form.useFormField('newLocation')

  const texts = useTexts()

  const reset = useCallback(async () => {
    // Reset the form
    api.reset('force')
    api.validate()

    // Reset the proposed label

    updateData({ proposedLabel: newLocationField.value.address })
    if (!map.current) return

    // get the last layer on the map
    const layer = map.current.getLayers().item(map.current.getLayers().getLength() - 1) as Layer<
      VectorSource<Geometry>
    >

    // return if layer is not found
    if (!layer) return

    // get the first feature on the layer
    const feature = layer?.getSource()?.getFeatures()?.[0]

    // return if feature is not found
    if (!feature) return

    // reset the pin position
    const geometry = feature.getGeometry() as Point
    const coords = gis.fromLatLngToCoordinate(newLocationField.value.latLng)
    geometry.setCoordinates(coords)

    // reset the map center
    map.current.getView().setCenter(coords)
  }, [map, api, updateData])

  const pristine = status === 'pristine'

  return (
    <FormFooter align="right" sx={{ paddingX: 3, paddingY: 2 }}>
      <FormActionButton
        testId="location-editor-modal_cancel-button"
        onClick={close}
        label={texts.cancel}
      />
      <FormActionButton
        testId="location-editor-modal_reset-button"
        onClick={reset}
        disabled={pristine}
        label={texts.reset}
      />
      <FormActionButton
        testId="location-editor-modal_confirm-changes-button"
        variant="contained"
        onClick={onSubmit}
        disabled={pristine}
        label={texts.confirmChanges}
      />
    </FormFooter>
  )
}
